import { NavType } from "@/types/common";

export const USER_TYPE = {
  media: "media",
  advertiser: "advertiser",
  non_mem: "non_mem",
  platform: "platform",
} as const;

export type UserRole = (typeof USER_TYPE)[keyof typeof USER_TYPE];

export enum ORGANIZATIONS_TYPE {
  // Media
  mediaDoohCMS = "media_dooh",
  mediaCMS = "media_cms",
  media = "media_adex",
  // Platform
  platformCompany = "platform_company",
  // Advertiser
  adClient = "ad_client",
  adAgency = "ad_agency",
  adCompany = "ad_company",
  govOffice = "gov_office",
  localGov = "local_gov",
  individual = "individual",
  organization = "organization",
}

export const INFO_SUPPLIER = {
  businessRegistrationNumber: "124-87-39651",
  businessName: "(주)엠앤엠네트웍스",
  representative: "김성원",
  address: "서울특별시 강남구 테헤란로 1길 28-11, 5층 5126호",
  contactPerson: "김성문",
  contactPersonEmail: "contact@mnmnetworks.com",
};

export const PAGE_URLS = {
  mediaList: "/dashboard/product-management/media",
  mediaDetail: "/dashboard/product-management/media/view",
  mediaCreate: "/dashboard/product-management/media/create",
  mediaEdit: "/dashboard/product-management/media/edit",
  mediaGroupList: "/dashboard/product-management/media-product",
  mediaProductDetail: "/dashboard/product-management/media-product/view",
  mediaProductCreate: "/dashboard/product-management/media-product/create",
  mediaProductEdit: "/dashboard/product-management/media-product/edit",
};

export const CUSTOMER_MANAGEMENT_PAGE_URLS = {
  advertisingAgency: "/dashboard/advertising-agency-details",
  mediaCompany: "/dashboard/media-company-details",
};

export const WEEK_OPTION_TYPE = [
  {
    labelKey: "all",
    value: "sunday,monday,tuesday,wednesday,thursday,friday,saturday",
  },
  { labelKey: "weekday", value: "monday,tuesday,wednesday,thursday,friday" },
  { labelKey: "weekend", value: "saturday,sunday" },
  { labelKey: "custom", value: "custom" },
] as const;

export const WEEK_OPTIONS = [
  {
    value: "sunday",
    valueNumber: "1",
    labelKey: "sunday",
    shortLabelKey: "sun",
  },
  {
    value: "monday",
    valueNumber: "2",
    labelKey: "monday",
    shortLabelKey: "mon",
  },
  {
    value: "tuesday",
    valueNumber: "3",
    labelKey: "tuesday",
    shortLabelKey: "tue",
  },
  {
    value: "wednesday",
    valueNumber: "4",
    labelKey: "wednesday",
    shortLabelKey: "wed",
  },
  {
    value: "thursday",
    valueNumber: "5",
    labelKey: "thursday",
    shortLabelKey: "thu",
  },
  {
    value: "friday",
    valueNumber: "6",
    labelKey: "friday",
    shortLabelKey: "fri",
  },
  {
    value: "saturday",
    valueNumber: "7",
    labelKey: "saturday",
    shortLabelKey: "sat",
  },
] as const;

export const USER_NAVIGATION: NavType = {
  advertiser: [
    {
      id: "ad-my-campaign",
      url: "/dashboard/my-campaign",
      title: "마이 캠페인",
      keyTranslate: "advertiser.top.myCampaign",
      pathGroup: "/dashboard/my-campaign",
    },
  ],
  media: [
    {
      id: "media-product-management",
      url: PAGE_URLS.mediaGroupList,
      title: "상품 관리",
      keyTranslate: "media.top.productManagement",
      pathGroup: "/dashboard/product-management",
    },
  ],
  platform: [
    {
      id: "platform-product-management",
      url: "/dashboard/product-management",
      title: "상품 관리",
      keyTranslate: "platform.top.productManagement",
      pathGroup: "/dashboard/product-management",
    },
    {
      id: "platform-customer-management",
      url: "/dashboard/customer-management",
      title: "고객 관리",
      keyTranslate: "platform.top.customerManagement",
      items: [
        {
          id: "platform-group-customer-info",
          groupName: "고객 정보 관리",
          keyTranslate: "platform.group.customerInfo",
          items: [
            {
              id: "platform-advertiser-agency",
              title: "광고주•광고대행사 정보",
              url: "/dashboard/advertiser-agency-list",
              keyTranslate: "platform.item.customerInfo.advertiserAgency",
            },
            {
              id: "platform-media-company",
              title: "미디어사 정보",
              url: "/dashboard/media-company-list",
              keyTranslate: "platform.item.customerInfo.mediaCompany",
            },
          ],
        },
        {
          id: "platform-group-service-operation",
          groupName: "서비스 운영 관리",
          keyTranslate: "platform.group.serviceOperation",
          items: [
            {
              id: "platform-announcement",
              title: "공지 사항",
              url: "/dashboard/announcement",
              keyTranslate: "platform.item.serviceOperation.announcement",
            },
            {
              id: "platform-faq",
              title: "FAQ",
              url: "/dashboard/faq",
              keyTranslate: "platform.item.serviceOperation.faq",
            },
          ],
        },
      ],
    },
  ],
};

export const SAVE_TEAM_STATUS = {
  approval: "approval",
  rejection: "rejection",
  waiting: "waiting",
};

export const SAVE_MEDIA_STATUS = {
  draft: "draft",
  unassigned: "unassigned",
  assigned: "assigned",
};

export const SAVE_MEDIA_PRODUCT_STATUS = {
  draft: "mp_draft",
  unpublished: "mp_unpublished",
  published: "mp_published",
  deactivated: "mp_deactivated",
  terminated: "mp_terminated",
  pendingLink: "mg_pending_link",
};

// Contract status constants and display labels
export const STATUS_CONTRACT = {
  RE_REVIEW: "re_review",
  // Registration (Order)
  //Before review
  UNREVIEWED: "unreviewed",
  UNDER_REVIEW: "under_review",
  // After review
  CAN_BUY_ALL: "can_buy_all",
  CAN_BUY_SOME: "can_buy_some",
  CAN_NOT_BUY: "can_not_buy",
  REVIEW_COMPLETED: "review_completed", // CAN_BUY_ALL / CAN_BUY_SOME / CAN_NOT_BUY

  // Negotiation
  UNDER_NEGOTIATION: "under_negotiation",
  NEGOTIATION_DONE: "negotiation_done",
  NEED_REVIEW: "need_review", // After negotiation, if review is needed before final decision
  // Contract
  SEND_CONTRACT: "send_contract",
  WAIT_SIGNING: "wait_signing",
  SIGNED: "signed",
  PLATFORM_TERMINATED: "platform_terminated",

  //Cancelled
  CANCELLED: "cancelled",

  //ContentUpload
  UPLOAD_WAITING: "upload_waiting",
  SETUP_WAITING: "setup_waiting",
  MATCHING_WAITING: "matching_waiting",
  CONFIRM_WAITING: "confirm_waiting",
  UPLOAD_COMPLETED: "upload_completed",
  CONFIRMED: "confirmed",

  // Operation
  OPERATION_IN_PROGRESS: "operation_in_progress",
  OPERATION_END: "operation_end",

  TOTAL_CONTRACT_MEDIA_ALL: "total_contract_media_all",
  TOTAL_CONTRACT_MEDIA: "total_contract_media",
  TOTAL_ORDER: "total_order",
  TOTAL_NEGOTIATION: "total_negotiation",
  TOTAL_CONTRACT: "total_contract",
};

export const CONSENT_TYPE = {
  CONSENT_EMAIL: "consent_email",
  CONSENT_NAME: "consent_name",
  CONSENT_MOBILE: "consent_mobile",
  CONSENT_TEL: "consent_tel",
  CONSENT_DEPARTMENT: "consent_department",
  CONSENT_POSITION: "consent_position",
  CONSENT_COMPANY_NAME: "consent_company_name",
  CONSENT_CERT_NUM: "consent_cert_num",
  CONSENT_COMPANY_ADDR: "consent_company_addr",
  CONSENT_COMPANY_TEL: "consent_company_tel",
  MARKETING_EMAIL: "marketing_email",
  MARKETING_SMS: "marketing_sms",
  TOS_AGREEMENT: "tos_argeement",
  PUSH_NOTIFICATION: "push_notification",
};
export const STATUS_CONTRACT_LABEL: Record<string, string> = {
  [STATUS_CONTRACT.TOTAL_ORDER]: "전체",
  [STATUS_CONTRACT.TOTAL_NEGOTIATION]: "전체",
  [STATUS_CONTRACT.TOTAL_CONTRACT]: "전체",
  [STATUS_CONTRACT.TOTAL_CONTRACT_MEDIA_ALL]: "전체",
  [STATUS_CONTRACT.TOTAL_CONTRACT_MEDIA]: "계약 중",
  [STATUS_CONTRACT.UNREVIEWED]: "검토 대기",
  [STATUS_CONTRACT.UNDER_REVIEW]: "검토 중",
  [STATUS_CONTRACT.CAN_BUY_ALL]: "구매 가능",
  [STATUS_CONTRACT.CAN_BUY_SOME]: "일부 구매 불가",
  [STATUS_CONTRACT.CAN_NOT_BUY]: "구매 불가",
  [STATUS_CONTRACT.REVIEW_COMPLETED]: "전달 완료",
  [STATUS_CONTRACT.UNDER_NEGOTIATION]: "협의 중",
  [STATUS_CONTRACT.NEGOTIATION_DONE]: "협의 완료",
  [STATUS_CONTRACT.CANCELLED]: "계약 파기",
  [STATUS_CONTRACT.SEND_CONTRACT]: "검토 필요",
  [STATUS_CONTRACT.WAIT_SIGNING]: "날인 필요",
  [STATUS_CONTRACT.SIGNED]: "계약 완료",
  [STATUS_CONTRACT.PLATFORM_TERMINATED]: "계약 파기",
  [STATUS_CONTRACT.NEED_REVIEW]: "검토 완료",
  // Operation
  [STATUS_CONTRACT.OPERATION_IN_PROGRESS]: "운영 중",
  [STATUS_CONTRACT.CONFIRMED]: "운영 대기",
  [STATUS_CONTRACT.UPLOAD_WAITING]: "운영 완료",
};

export const STATUS_CONTRACT_DASHBOARD_LABEL = {
  [STATUS_CONTRACT.TOTAL_ORDER]: "신청",
  [STATUS_CONTRACT.TOTAL_NEGOTIATION]: "협의",
  [STATUS_CONTRACT.TOTAL_CONTRACT]: "협의 중",
  [STATUS_CONTRACT.TOTAL_CONTRACT_MEDIA]: "협의 중",
};

export const BG_COLOR_STATUS_DASHBOARD = {
  [STATUS_CONTRACT.UNDER_REVIEW]: {
    label: "검토 중",
    text: "text-blue-700",
    bg: "bg-blue-200",
  },
  [STATUS_CONTRACT.CAN_BUY_SOME]: {
    label: "구매 가능",
    text: "text-[#3AA23F]",
    bg: "bg-[#E4F8E3]",
  },
  [STATUS_CONTRACT.RE_REVIEW]: {
    label: "재검토 필요",
    text: "text-[#FF9500]",
    bg: "bg-[#FFF4DE]",
  },
  [STATUS_CONTRACT.CAN_NOT_BUY]: {
    label: "구매 불가",
    text: "text-[#EF4444]",
    bg: "bg-red-50",
  },
  [STATUS_CONTRACT.UNDER_NEGOTIATION]: {
    label: "협의 중",
    text: "text-gray-200",
    bg: "bg-blue-200",
  },
  [STATUS_CONTRACT.NEGOTIATION_DONE]: {
    label: "협의 완료",
    text: "text-[#3AA23F]",
    bg: "bg-[#E4F8E3]",
  },
  [STATUS_CONTRACT.WAIT_SIGNING]: {
    label: "검토 필요",
    text: "text-[#EF4444]",
    bg: "bg-red-50",
  },
  [STATUS_CONTRACT.SIGNED]: {
    label: "날인 필요",
    text: "text-[#3AA23F]",
    bg: "bg-[#E4F8E3]",
  },
};

export const AD_TYPES = [
  { label: "adTypes.normal", value: "normal_ad" },
  { label: "adTypes.sync", value: "condition_ad" },
  { label: "adTypes.time", value: "condition_time_ad" },
];

export const STATUS_BOARD = {
  review: {
    label: "검토 중",
    color: "text-blue-700",
    bgColor: "bg-blue-100",
  },
  available: {
    label: "승인",
    color: "text-green-700",
    bgColor: "bg-green-100",
  },
};

export type StatusKey = keyof typeof STATUS_BOARD;

export enum PAYMENT_TYPE {
  DEPOSIT = "deposit",
  BALANCE = "balance",
}

export const PAYMENT_RESOLVED_TYPE = {
  BEFORE_BILLING: "Before Billing",
  AWAITING_PAYMENT: "Awaiting Payment",
  PAYMENT_COMPLETED: "Payment Completed",
  PENDING: "Pending",
};

export const PAYMENT_RESOLVED_TYPE_MEDIA = {
  ISSUANCE_REQUIRED: "Issuance Required",
  AWAITING_PAYMENT: "Awaiting Payment",
  PAYMENT_COMPLETED: "Payment Completed",
  SETTLEMENT_COMPLETED: "Settlement Completed",
};

export const USER_TYPE_MAP = {
  [USER_TYPE.advertiser]: "ad",
  [USER_TYPE.media]: "media",
  [USER_TYPE.platform]: "platform",
  [USER_TYPE.non_mem]: "non_mem",
};

export const SETTLEMENT_CLASSIFICATION = {
  [PAYMENT_TYPE.DEPOSIT]: "계약금",
  [PAYMENT_TYPE.BALANCE]: "잔금",
};

export type UserType = keyof typeof USER_TYPE;
export type UserTypeMap = typeof USER_TYPE_MAP;
export type SettlementClassification = keyof typeof SETTLEMENT_CLASSIFICATION;
export type PaymentResolvedType = keyof typeof PAYMENT_RESOLVED_TYPE;

export const SCREEN_RESOLUTION_VALUE: Record<
  string,
  { width: number; height: number }
> = {
  resolution_uhd: { width: 3840, height: 2160 },
  resolution_qhd: { width: 2560, height: 1440 },
  resolution_fhd: { width: 1920, height: 1080 },
  resolution_hd: { width: 1280, height: 720 },
  resolution_custom: { width: 0, height: 0 },
};

export const PROFILE = {
  advertiser: [
    {
      labelKey: "inquiryHistory",
      url: "/dashboard/ad-inquiry-history",
      keyTranslate: "profileDropdown",
    },
    {
      labelKey: "teamManagement",
      url: "/dashboard/my-team-advertiser",
      keyTranslate: "profileDropdown",
    },
    {
      labelKey: "accountSettings",
      url: "/user/account-settings",
      keyTranslate: "profileDropdown",
    },
  ],

  media: [
    {
      labelKey: "inquiryHistory",
      url: "/dashboard/media-inquiry-history",
      keyTranslate: "profileDropdown",
    },
    {
      labelKey: "managerManagement",
      url: "/dashboard/pic-team",
      keyTranslate: "profileDropdown",
    },
    {
      labelKey: "accountSettings",
      url: "/user/account-settings",
      keyTranslate: "profileDropdown",
    },
  ],

  platform: [
    {
      labelKey: "inquiryHistory",
      url: "/dashboard/platform-inquiry-history",
      keyTranslate: "profileDropdown",
    },
    {
      labelKey: "managerManagement",
      url: "/dashboard/pic-team",
      keyTranslate: "profileDropdown",
    },
    {
      labelKey: "accountSettings",
      url: "/user/account-settings",
      keyTranslate: "profileDropdown",
    },
  ],
};

export enum AgeGroup {
  A10S = "A10S",
  A20S = "A20S",
  A30S = "30s",
  A40S = "A30S",
  A50S = "A40S",
  A60S = "A60S",
  A70S = "A70S",
  R0_9 = "R0_9",
  R10_14 = "R10_14",
  R15_19 = "R15_19",
  R20_24 = "R20_24",
  R25_29 = "R25_29",
}

export enum AgeRange {
  R0_9 = "0_9",
  R10_14 = "10_14",
  R15_19 = "15_19",
  R20_24 = "20_24",
  R25_29 = "25_29",
}

export const operationListOrder = [
  { value: "newest", labelKey: "operationSort.newest" },
  { value: "oldest", labelKey: "operationSort.oldest" },
  { value: "ad_start_date", labelKey: "operationSort.adStartDate" },
  { value: "ad_end_date", labelKey: "operationSort.adEndDate" },
  { value: "advertiser", labelKey: "operationSort.advertiser" },
  { value: "contract_name", labelKey: "operationSort.contractName" },
] as const;

export const operationHistorySearch = [
  {
    value: "media_product_name",
    labelKey: "operationHistory.mediaProductName",
  },
  {
    value: "contract_name",
    labelKey: "operationHistory.contractName",
  },
  {
    value: "organizations_name",
    labelKey: "operationHistory.advertiser",
  },
] as const;

export const OPTIONS_SEARCH_SECOND = [
  { value: "contract_name", label: "계약명" },
  { value: "organizations_name", label: "광고주" },
  { value: "applicant_name", label: "신청자" },
];

export const OPTIONS_SEARCH_THIRD = [
  { value: "contract_name", label: "계약명" },
  { value: "organizations_name", label: "광고주명" },
];

export const OPTIONS_SEARCH_FOURTH = [
  { value: "contract_name", label: "계약명" },
  { value: "organizations_name", label: "광고주" },
];

export const STATUS_DISABLED = [
  STATUS_CONTRACT.NEGOTIATION_DONE,
  STATUS_CONTRACT.SEND_CONTRACT,
  STATUS_CONTRACT.WAIT_SIGNING,
  STATUS_CONTRACT.PLATFORM_TERMINATED,
];

export const STATUS_ALLOWED_PAYMENT = [
  STATUS_CONTRACT.NEGOTIATION_DONE,
  STATUS_CONTRACT.SEND_CONTRACT,
  STATUS_CONTRACT.WAIT_SIGNING,
  STATUS_CONTRACT.SIGNED,
  STATUS_CONTRACT.PLATFORM_TERMINATED,
];

export const statusOptions = [
  { label: "계약서 작성 필요", value: STATUS_CONTRACT.NEGOTIATION_DONE },
  { label: "계약서 작성 완료", value: STATUS_CONTRACT.SEND_CONTRACT },
  { label: "계약서 날인 대기", value: STATUS_CONTRACT.WAIT_SIGNING },
  { label: "계약서 완료", value: STATUS_CONTRACT.SIGNED },
  { label: "계약 파기", value: STATUS_CONTRACT.PLATFORM_TERMINATED },
];

export const PEAK_GENDER_AGE_MAP: Record<
  number,
  { gender: "male" | "female"; ageKey: string }
> = {
  0: { gender: "female", ageKey: "under_10" },
  1: { gender: "female", ageKey: "10_14" },
  2: { gender: "female", ageKey: "15_19" },
  3: { gender: "female", ageKey: "20_24" },
  4: { gender: "female", ageKey: "25_29" },
  5: { gender: "female", ageKey: "30s" },
  6: { gender: "female", ageKey: "40s" },
  7: { gender: "female", ageKey: "50s" },
  8: { gender: "female", ageKey: "60s" },
  9: { gender: "female", ageKey: "70_plus" },

  10: { gender: "male", ageKey: "under_10" },
  11: { gender: "male", ageKey: "10_14" },
  12: { gender: "male", ageKey: "15_19" },
  13: { gender: "male", ageKey: "20_24" },
  14: { gender: "male", ageKey: "25_29" },
  15: { gender: "male", ageKey: "30s" },
  16: { gender: "male", ageKey: "40s" },
  17: { gender: "male", ageKey: "50s" },
  18: { gender: "male", ageKey: "60s" },
  19: { gender: "male", ageKey: "70_plus" },
};

export const PEAK_GENDER_AGE_LABEL_MAP: Record<
  number,
  { gender: "male" | "female"; ageKey: string }
> = {
  0: { gender: "female", ageKey: "under_10" },
  1: { gender: "female", ageKey: "10_14" },
  2: { gender: "female", ageKey: "15_19" },
  3: { gender: "female", ageKey: "20_24" },
  4: { gender: "female", ageKey: "25_29" },
  5: { gender: "female", ageKey: "30s" },
  6: { gender: "female", ageKey: "40s" },
  7: { gender: "female", ageKey: "50s" },
  8: { gender: "female", ageKey: "60s" },
  9: { gender: "female", ageKey: "70_plus" },

  10: { gender: "male", ageKey: "under_10" },
  11: { gender: "male", ageKey: "10_14" },
  12: { gender: "male", ageKey: "15_19" },
  13: { gender: "male", ageKey: "20_24" },
  14: { gender: "male", ageKey: "25_29" },
  15: { gender: "male", ageKey: "30s" },
  16: { gender: "male", ageKey: "40s" },
  17: { gender: "male", ageKey: "50s" },
  18: { gender: "male", ageKey: "60s" },
  19: { gender: "male", ageKey: "70_plus" },
};

export const SORT_ORDER = {
  NEWEST: "newest",
  OLDEST: "oldest",
  ALPHABETICAL: "alphabetical",
};
